<template>
    <div class="gallery-grid">
        <ol
            class="gallery-list"
            :style="getListStyle()">
            <li
                class="gallery-thumb"
                :style="getThumbStyle(galleryCell)"
                v-for="galleryCell in galleryCells">
                <router-link
                    :to="getGalleryLocation(galleryCell.gallery)"
                    @click.native="emitClick">
                    <img :src="getGalleryThumbUrl(galleryCell.gallery)">
                    <img
                        src="/static/static/img/ranks/rank-recruiter-red.svg"
                        class="recruiter-visibility"
                        v-if="isGalleryVisibleToRecruiter(galleryCell.gallery)">
                </router-link>
            </li>
        </ol>
    </div>
</template>

<script>
    import GridPacker from '../../packages/grid-packer';

    // Galleries with likes above this threshold are 2 cells wide
    const likeThreshold1 = 20;

    // Galleries with likes above this threshold are 3 cells wide
    const likeThreshold2 = 40;

    export default {
        props: {
            galleries: Array,
            countPerRow: {
                type: Number,
                default: 4,
                validator: value => {
                    return value > 0 && Math.floor(value) === value;
                }
            },
            gutterWidth: {
                // Measured in pixels
                type: Number,
                default: 0
            },
            thresholdSizing: {
                // If true, galleries may be larger depending on likes
                type: Boolean,
                default: false
            },
        },

        data() {
            return {
                galleryCells: [],
                cellWidth: 0,
            };
        },

        computed: {
            gridPacker() {
                return new GridPacker(this.countPerRow);
            },

            userRoles() {
                return JSON.parse(localStorage.roles);
            },

            canViewHighlights() {
                return this.userRoles.includes('OnlineDirector');
            },
        },

        mounted() {
            window.addEventListener('resize', this.onWindowResize);
            this.updateCellWidth();
            this.repack();
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.onWindowResize);
        },

        methods: {
            onWindowResize() {
                // Not sure if $el width will be updated immediately, so wait
                // until the next DOM update.
                this.$nextTick(() => {
                    this.updateCellWidth();
                });
            },

            emitClick(ev) {
                this.$emit('click', ev);
            },

            getGalleryLocation(gallery) {
                return {
                    name: 'gallery',
                    params: {
                        galleryId: gallery.id
                    }
                };
            },

            getGalleryThumbUrl(gallery) {
                let url = gallery.thumbnailMedia.url;
                if (url) {
                    // TODO: this is obsolete once thumbnail cropping is deployed
                    url = url.replace(
                        'ttto-gallery-images-full',
                        'ttto-gallery-images-thumbs'
                    );
                }
                return url;
            },

            getListStyle() {
                const h = this.galleryCells.reduce((max, cell) => {
                    return Math.max(max, cell.y + cell.height);
                }, 0);
                return {
                    height: `${h * this.cellWidth + ((h - 1) * this.gutterWidth)}px`,
                };
            },

            getThumbStyle(cell) {
                const fullCellWidth = this.cellWidth + this.gutterWidth;
                const x1 = cell.x * fullCellWidth;
                const y1 = cell.y * fullCellWidth;
                const x2 = (cell.x + cell.width) * fullCellWidth - this.gutterWidth;
                const y2 = (cell.y + cell.height) * fullCellWidth - this.gutterWidth;
                return {
                    top:    `${y1}px`,
                    left:   `${x1}px`,
                    width:  `${x2 - x1}px`,
                    height: `${y2 - y1}px`,
                };
            },

            updateCellWidth() {
                let widthAvailable = this.$el.clientWidth;
                widthAvailable -= this.gutterWidth * (this.countPerRow - 1);
                this.cellWidth = widthAvailable / this.countPerRow;
            },

            repack() {
                const items = this.galleries.map(g => {
                    let likeCount = g.likeCount;
                    if (g.likes) {
                        likeCount = g.likes.length;
                    }

                    let size = 1;
                    if (this.thresholdSizing) {
                        if (likeCount >= likeThreshold1) {
                            size = Math.min(2, this.countPerRow);
                        }
                        if (likeCount >= likeThreshold2) {
                            size = Math.min(3, this.countPerRow);
                        }
                    }

                    return {
                        width: size,
                        height: size,
                        gallery: g,
                    };
                });

                this.galleryCells = this.gridPacker.pack(items);
            },

            /**
             * Gets offset in px from the top of the grid to the first hole in
             * the grid. If there are no holes in the grid, the height of grid
             * is returned.
             *
             * @returns {number} px
             */
            getOffsetToEmpty() {
                const holes = this.gridPacker.findHoles(this.galleryCells);
                if (holes.length > 0) {
                    const minHoleY = holes.reduce((accum, current) => {
                        return Math.min(accum, current.y);
                    }, Infinity);
                    const cellOffset = minHoleY * this.cellWidth;
                    const gutterOffset = (minHoleY - 1) * this.gutterWidth;
                    return cellOffset + gutterOffset;
                } else {
                    return this.$el.clientHeight;
                }
            },

            isGalleryVisibleToRecruiter(gallery) {
                return (
                    this.canViewHighlights &&
                    gallery.highlighted
                );
            },
        },

        watch: {
            galleries: {
                handler: function() {
                    this.updateCellWidth();
                    this.repack();
                },
                deep: true,
            },

            countPerRow: function() {
                this.updateCellWidth();
                this.repack();
            },

            gutterWidth: function() {
                this.updateCellWidth();
            },

            thresholdSizing: function() {
                this.updateCellWidth();
                this.repack();
            },
        },
    }
</script>

<style lang="scss" scoped>
    .gallery-list {
        list-style-type: none;
        margin: 0;
        padding: 0;
        position: relative;
        overflow: hidden;

        .gallery-thumb {
            background-color: #222;
            display: block;
            position: absolute;

            a {
                display: block;
                height: 100%;
                position: relative;
            }

            img {
                width: 100%;
                height: 100%;
            }

            .recruiter-visibility {
                position: absolute;
                bottom: 4px;
                right: 4px;
                width: 24px;
                height: 24px;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// GalleryGrid.vue?556e8ce1